import NotFoundPage from 'components/pages/error/404';
import App from 'partners/containers/App';
import Main from 'partners/containers/Main';
import Signup from 'partners/containers/Signup';
import SignupVerify from 'partners/containers/SignupVerify';
import Login from 'partners/containers/Login';
import AcceptStore from 'partners/containers/AcceptStore';
import AcceptStoreConnect from 'partners/containers/AcceptStoreConnect';
import Home from 'partners/containers/Home';
import Apps from 'partners/containers/Apps';
import NewApp from 'partners/containers/NewApp';
import ViewApp from 'partners/containers/ViewApp';
import Stores from 'partners/containers/Stores';
import Earnings from 'partners/containers/Earnings';
import PartnerPage from 'partners/containers/PartnerPage';
import UserSettings from 'partners/containers/UserSettings';

export function configureRoutes(store) {
  const routeHook = (handler, params) => {
    if (typeof handler !== 'function') {
      return undefined;
    }

    return async (...args) => {
      // router hook can have 3 or 4 args:
      // also, args need to be defined for callback to have an effect on order
      // router.onChange(prevState, nextState, replace, callback?)
      // router.onEnter(nextState, replace, callback?)
      // router.onLeave(nextState, replace, callback?)
      const nextState = args[args.length - 3];
      const replace = args[args.length - 2];
      const callback = args[args.length - 1];

      if (params) {
        await handler(store, params, nextState, replace);
      } else {
        await handler(store, nextState, replace);
      }

      callback();
    };
  };

  const path = (uri) => `/:partnerId${uri}`;

  const container = (component, params) => ({
    component,
    onEnter: routeHook(component.onEnter, params),
    onChange: routeHook(component.onChange, params),
    onLeave: routeHook(component.onLeave, params),
  });

  return {
    ...container(App),
    childRoutes: [
      {
        path: '/signup',
        ...container(Signup),
      },
      {
        path: '/signup/verify-email/:id',
        ...container(SignupVerify),
      },
      {
        path: '/signup/join/:key',
        ...container(Login),
        page: 'join',
      },

      {
        path: '/login',
        ...container(Login),
        page: 'login',
      },
      {
        path: '/recover',
        ...container(Login),
        page: 'recover',
      },
      {
        path: '/recover/:resetKey',
        ...container(Login),
        page: 'recover',
      },
      // A store's partner invite (see `AcceptStore` for the steps)
      {
        path: '/accept-store/:code',
        ...container(AcceptStore),
        step: 'start',
      },
      {
        path: '/accept-store/:code/select',
        ...container(AcceptStore),
        step: 'select',
      },

      // Must stay ahead of `path('')`, whose children would otherwise be
      // reached only by backtracking
      {
        path: path('/login'),
        ...container(Login),
        page: 'login',
      },
      {
        path: path('/join/:key'),
        ...container(Login),
        page: 'join',
      },
      // Where the login lands back (prefixed with the session's partner)
      {
        path: path('/accept-store/:code'),
        ...container(AcceptStore),
        step: 'login',
      },
      {
        path: path(''),
        ...container(Main),
        indexRoute: { ...container(Home) },
        childRoutes: [
          {
            path: path('/stores'),
            ...container(Stores),
          },
          {
            path: path('/accept-store/:code/connect'),
            ...container(AcceptStoreConnect),
          },
          {
            path: path('/apps'),
            ...container(Apps),
          },
          {
            path: path('/apps/new'),
            ...container(NewApp),
          },
          {
            path: path('/apps/:id'),
            ...container(ViewApp),
          },
          {
            path: path('/earnings'),
            ...container(Earnings),
          },
          {
            path: path('/partner-page'),
            ...container(PartnerPage),
          },
          {
            path: path('/users'),
            section: 'users',
            ...container(UserSettings),
          },
          {
            path: path('/users/:id'),
            section: 'user',
            ...container(UserSettings),
          },
          {
            path: path('/settings/profile'),
            onEnter: (nextState, replace) =>
              replace(`/${nextState.params.partnerId}/partner-page`),
          },
          {
            path: path('/settings/users'),
            onEnter: (nextState, replace) =>
              replace(`/${nextState.params.partnerId}/users`),
          },
          {
            path: path('/settings/users/:id'),
            onEnter: (nextState, replace) =>
              replace(
                `/${nextState.params.partnerId}/users/${nextState.params.id}`,
              ),
          },
        ],
      },
      {
        path: '*',
        component: NotFoundPage,
      },
    ],
  };
}
